import { useState } from "react";
import {
  Box,
  IconButton,
  Typography,
  Dialog,
  DialogContent,
  Button,
  useTheme,
  CircularProgress,
} from "@mui/material";
import { QrCode, Email, WhatsApp, FacebookOutlined } from "@mui/icons-material";
import QRCode from "react-qr-code";
import { tokens } from "../../../../../../theme";
import { IThemeConfig } from "../types/memorial-types";
import { Copy } from "lucide-react";
import { showSuccessToast } from "../../../../../../components/common/ShowToast";
import SimCardDownloadOutlinedIcon from "@mui/icons-material/SimCardDownloadOutlined";
import AddToCalendarButton from "./AddToCalendar";
import type { CalendarEvent } from "../types/AddtoCalendarutils";
import ChemaDonationDialog from "./ChemaDonationDialog";

interface ShareControlsProps {
  theme: IThemeConfig;
  streamData: {
    id: number;
    website_stream_link?: string;
    title?: string;
    booking?: {
      id: number;
      service: string;
      deceased?: Array<{
        id: number;
        person_image?: string;
      }>;
    };
  } | null;
  donationsEnabled?: boolean;
  streamProgram?: string | null;
  downloadingProgram?: boolean;
  handleDownloadProgram?: () => void;
  onDonationSuccess?: () => void;
  calendarEvent?: CalendarEvent | null;
}

export default function ShareControls({
  streamData,
  theme,
  donationsEnabled = false,
  streamProgram,
  handleDownloadProgram,
  downloadingProgram,
  onDonationSuccess,
  calendarEvent,
}: ShareControlsProps) {
  const muiTheme = useTheme();
  const colors = tokens(muiTheme.palette.mode);
  const [openQr, setOpenQr] = useState(false);
  const [openDonationDialog, setOpenDonationDialog] = useState(false);

  const handleWhatsAppShare = () => {
    if (!streamData?.website_stream_link) return;
    const isConference = streamData?.booking?.service === "Conference";
    const messageTemplate = isConference
      ? `Join us for the ${streamData.title}.\n\nWatch the livestream here: ${streamData.website_stream_link}`
      : `It is with great sadness that we share the livestream link for the funeral ${streamData.title}.\n\nYou can view it here: ${streamData.website_stream_link}`;
    const text = encodeURIComponent(messageTemplate);
    window.open(`https://wa.me/?text=${text}`, "_blank");
  };

  const handleEmailShare = () => {
    if (!streamData?.website_stream_link) return;
    const isConference = streamData?.booking?.service === "Conference";
    const subject = encodeURIComponent(
      isConference
        ? `${streamData.title} - Livestream Link`
        : `Funeral Service for ${streamData.title} - Livestream Link`,
    );
    const body = encodeURIComponent(
      isConference
        ? `Dear colleagues,\n\nWe are sharing the livestream link for ${streamData.title}.\n\nYou can join the livestream here: ${streamData.website_stream_link}\n\nWe look forward to your participation.\n\nSincerely,`
        : `Dear friends and family,\n\nWe are sharing the livestream link for the funeral service of ${streamData.title}\n\nYou can view the livestream here: ${streamData.website_stream_link}\n\nThank you for your thoughts and support during this difficult time.\n\nSincerely,`,
    );
    window.open(`mailto:?subject=${subject}&body=${body}`, "_blank");
  };

  const handleCopyUrl = async () => {
    if (!streamData?.website_stream_link) return;
    try {
      await navigator.clipboard.writeText(streamData.website_stream_link);
      showSuccessToast("Link Copied Successfully");
    } catch (err) {
      console.error("Failed to copy URL: ", err);
    }
  };

  const handleOpenDonationDialog = () => {
    if (!donationsEnabled) return;
    setOpenDonationDialog(true);
  };

  const handleCloseDonationDialog = () => setOpenDonationDialog(false);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between",
        width: "100%",
        mb: 2,
      }}
    >
      {/* ── Left: Download Program + Add to Calendar + Donate ── */}
      <Box
        sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}
      >
        {/* Download Program */}
        <Button
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            borderRadius: "60px",
            py: 0.6,
            px: 1.5,
            boxShadow: 0,
            textTransform: "none",
            opacity: streamProgram ? 1 : 0.5,
          }}
          variant="contained"
          size="small"
          onClick={handleDownloadProgram}
          disabled={!streamProgram || downloadingProgram}
        >
          {downloadingProgram ? (
            <CircularProgress size={18} sx={{ color: "#fff" }} />
          ) : (
            <>
              <SimCardDownloadOutlinedIcon
                sx={{ color: "#fff", fontSize: 18 }}
              />
              <Typography
                sx={{ color: "#fff", fontWeight: "bold", fontSize: "0.8rem" }}
              >
                Download Program
              </Typography>
            </>
          )}
        </Button>

        {/* Add to Calendar — only renders when calendarEvent prop is passed */}
        {calendarEvent && (
          <AddToCalendarButton
            event={calendarEvent}
            variant="outlined"
            size="small"
            label="Add to Calendar"
          />
        )}

        {/* Donate */}
        {streamData?.booking?.service !== "Conference" && donationsEnabled && (
          <Button
            variant="contained"
            onClick={handleOpenDonationDialog}
            sx={{
              borderRadius: "50px",
              textTransform: "none",
              fontWeight: "bold",
              px: 3,
              backgroundColor: "#1d7a6a",
              "&:hover": { backgroundColor: "#155f52" },
            }}
          >
            Donate
          </Button>
        )}
      </Box>

      {/* ── Right: Social share icons ── */}
      <Box sx={{ display: "flex", gap: 1.5 }}>
        <IconButton
          sx={{
            backgroundColor: "#25D366",
            color: theme.surface,
            "&:hover": { backgroundColor: "#1DA851" },
            mr: 1,
          }}
          onClick={handleWhatsAppShare}
        >
          <WhatsApp />
        </IconButton>

        <IconButton
          sx={{
            backgroundColor: "info.main",
            color: theme.surface,
            "&:hover": { backgroundColor: "info.light" },
            mr: 1,
          }}
        >
          <FacebookOutlined />
        </IconButton>

        <IconButton
          sx={{
            backgroundColor: "#EA4335",
            color: theme.surface,
            "&:hover": { backgroundColor: "#D63A2C" },
            mr: 1,
          }}
          onClick={handleEmailShare}
        >
          <Email />
        </IconButton>

        <IconButton
          sx={{
            backgroundColor: colors.primary[900],
            color: colors.primary[700],
            mr: 1,
          }}
          onClick={() => setOpenQr(true)}
        >
          <QrCode />
        </IconButton>

        <IconButton
          onClick={handleCopyUrl}
          sx={{
            backgroundColor: "#000",
            color: "#fff",
            "&:hover": { backgroundColor: "#807d7dff" },
          }}
        >
          <Copy />
        </IconButton>
      </Box>

      {/* QR Dialog */}
      <Dialog open={openQr} onClose={() => setOpenQr(false)}>
        <DialogContent
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 2,
          }}
        >
          <QRCode value={streamData?.website_stream_link || ""} size={200} />
        </DialogContent>
      </Dialog>

      {/* Donation / Chema Dialog */}
      <ChemaDonationDialog
        open={openDonationDialog}
        onClose={handleCloseDonationDialog}
        streamId={streamData?.id ?? null}
        streamTitle={streamData?.title}
        deceasedImage={streamData?.booking?.deceased?.[0]?.person_image}
        onDonationSuccess={onDonationSuccess}
      />
    </Box>
  );
}
